import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import TopBar from '../components/Wiki/TopBar';
import WikiLayout, { DOC } from '../layouts/WikiLayout';
import api from '../lib/api';
import { freshness } from '../utils/freshness';
import type { WikiPageTrashed } from '../types/wiki';

type WikiTrashProps = { pages: WikiPageTrashed[] };

type WikiTrashComponent = React.FC<WikiTrashProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 回收站。刻意只有列表 + 恢复：没有搜索、没有分页、没有批量、没有「彻底删除」
// —— 照 TodoTrash.tsx 的先例，单人应用规模下那些都是提前优化。
//
// 与 /todo/trash 的一处区别：wiki 没有「30 天后自动清除」那套（第一期没有
// PurgeDiscardedTasksJob 的对应物），所以文案不许承诺它。
const WikiTrash: WikiTrashComponent = ({ pages }) => {
  const [restoringKey, setRestoringKey] = useState<string | null>(null);

  // 走 api 客户端而不是 router.post：Web::Internal 回的是纯 JSON，用 Inertia
  // 访问打过去会直接抛「All Inertia requests must receive a valid Inertia
  // response」并把整页糊上错误框。
  const restore = async (urlKey: string) => {
    setRestoringKey(urlKey);
    try {
      await api.wikiPages.restore(urlKey);
      // 恢复完停在回收站页，只是那一条不见了 —— 连着恢复几条时不必来回跳。
      //
      // 整块 reload（不带 only）：边栏那个回收站计数和「最近编辑」都要跟着变，
      // 而它们在共享 prop wikiNav 里 —— 带 only 就会把它滤掉。
      router.reload();
    } finally {
      setRestoringKey(null);
    }
  };

  return (
    <>
      <TopBar trail={[{ label: '回收站' }]} />

      <div className={DOC}>
        <h1 className="text-[26px] font-bold tracking-tight">回收站</h1>
        <p className="mb-5 mt-2 text-[13.5px] leading-relaxed text-todo-muted">
          删掉的页面留在这里，随时能恢复。agent 不能删页面 —— 删除只在你这一侧。
        </p>

        {pages.length === 0 ? (
          <p className="text-[13.5px] text-todo-muted">回收站是空的。</p>
        ) : (
          <div>
            {pages.map((page) => (
              <div
                key={page.url_key}
                className="-mx-2.5 flex items-center gap-2.5 rounded-[7px] px-2.5 py-2.5
                           hover:bg-wiki-sideHover"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14.5px] text-todo-ink">{page.title}</div>
                  {/* 「删于 X」（设计稿）。天数由服务端算（days_since_delete）——
                      与 days_since_update 同一个理由，见 WikiPagePresenter#trashed。
                      这一行是判断「这条是不是我刚删错的那个」的唯一线索。 */}
                  {page.days_since_delete !== null && (
                    <div className="mt-0.5 text-[12.5px] text-todo-muted">
                      删于 {freshness(page.days_since_delete, page.deleted_at)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => restore(page.url_key)}
                  disabled={restoringKey === page.url_key}
                  className="shrink-0 rounded-[7px] border border-todo-line px-3 py-1
                             text-[12.5px] text-todo-ink2 hover:bg-todo-card2
                             disabled:opacity-50"
                >
                  {restoringKey === page.url_key ? '恢复中…' : '恢复'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

WikiTrash.layout = (page: React.ReactNode) => <WikiLayout>{page}</WikiLayout>;

export default WikiTrash;
